import { useState } from "react";
import { Calendar, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import { SingleDateCalendar } from "./single-date-calendar";

export interface SingleDatePickerProps {
  date: string;
  onDateChange: (date: string) => void;
  placeholder?: string;
  className?: string;
  /** "YYYY-MM-DD" — later days are not selectable. */
  maxDate?: string;
  /** Extra classes for the read-only input (e.g. an error border). */
  inputClassName?: string;
  id?: string;
  /** Opt-in CLEAR: with a date picked, an × replaces the calendar icon and
   *  empties the field without opening the calendar (filter drawers — a
   *  form's required date keeps no ×). */
  onClear?: () => void;
  /** Month + year dropdowns in the calendar header (see SingleDateCalendar). */
  yearPicker?: boolean;
}

export function SingleDatePicker({
  date,
  onDateChange,
  placeholder = "dd / mm / yyyy",
  className = "",
  maxDate,
  inputClassName = "",
  id,
  onClear,
  yearPicker = false,
}: SingleDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Format date from YYYY-MM-DD to dd / mm / yyyy
  const formatDate = (dateString: string): string => {
    if (!dateString) return "";

    if (dateString.includes("-")) {
      const [year, month, day] = dateString.split("-");
      return `${day} / ${month} / ${year}`;
    }

    return dateString;
  };

  const handleDateChange = (newDate: string) => {
    onDateChange(newDate);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className={`relative cursor-pointer ${className}`}>
          <Input
            id={id}
            type="text"
            value={formatDate(date)}
            placeholder={placeholder}
            readOnly
            className={`w-full h-10 bg-[#fcfcfc] border border-[var(--color-black-2)] rounded-[12px] px-3 py-2 pr-10 text-[14px] leading-[1.35] tracking-[-0.56px] text-[#505e59] font-normal cursor-pointer ${inputClassName}`}
          />
          {onClear && date ? (
            <button
              type="button"
              aria-label="Clear date"
              title="Clear date"
              // The field is the popover's trigger — clear without opening it.
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded-full text-[#697571] transition-colors hover:bg-[#eef2f0] hover:text-[#051912] focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]"
            >
              <X className="size-4" />
            </button>
          ) : (
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#697571] pointer-events-none" />
          )}
        </div>
      </PopoverTrigger>
      {/* Fixed width, deliberately. The calendar root is `w-full` and its day
          cells are `aspect-square`, so with `w-auto` the popover took its width
          from whatever container it was opened in — fine in the narrow filter
          drawer, but inside a 980px modal the cells grew into huge squares and
          the calendar overflowed the dialog. A date grid has no reason to be
          elastic; 288px is seven ~34px columns plus padding. */}
      <PopoverContent className="w-[288px] p-4" align="start">
        <SingleDateCalendar
          date={date}
          onDateChange={handleDateChange}
          maxDate={maxDate}
          yearPicker={yearPicker}
        />
      </PopoverContent>
    </Popover>
  );
}
